import { useMemo } from 'react';
import type { AnalysisSubject } from 'venn-utils';
import { getSubjectFunds } from 'venn-utils';
import { prefetchBenchmarkQuery, useBenchmarkQuery } from '../../hooks/useBenchmarkQuery';
import { useProxyDataQuery, useRangeAnalysisQuery } from '../../hooks';
import { isNil, mapValues } from 'lodash';
import type { SubjectRangeAnalysis } from 'venn-api';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';

export const useBulkFundData = (subject: AnalysisSubject) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const uniqPortfolioFunds = useMemo(() => getSubjectFunds(subject, false), [subject]);
  const uniqFundIds = useMemo(() => {
    // IDs are sorted for cache key stability
    if (
      typeof subject.activeBenchmarkId === 'string' &&
      !uniqPortfolioFunds.find((f) => f.id === subject.activeBenchmarkId)
    ) {
      return [...uniqPortfolioFunds.map((f) => f.id), subject.activeBenchmarkId].sort();
    }
    return uniqPortfolioFunds.map((f) => f.id).sort();
  }, [uniqPortfolioFunds, subject.activeBenchmarkId]);

  // Pre-fetch to prevent suspense waterfall
  prefetchBenchmarkQuery(subject);
  // No need to suspend for rangeAnalysisQuery, because we have per-row loading states for it.
  const { rangeAnalysis: rangeAnalysisResult, analysisRequest, isFetching } = useRangeAnalysisQuery(subject);
  // Force a loading state when rangeAnalysis is fetching by setting rangeAnalysis back to undefined.
  const rangeAnalysis = isFetching ? undefined : rangeAnalysisResult;
  const proxyDataByFund = useProxyDataQuery(uniqFundIds, proxySetId);

  const returnsProxyDataByFund = useMemo(
    () => mapValues(proxyDataByFund, (data) => data?.returns ?? ([] as const)),
    [proxyDataByFund],
  );

  const holdingsProxyDataByFund = useMemo(
    () => mapValues(proxyDataByFund, (data) => data?.holdings),
    [proxyDataByFund],
  );

  const benchmarkInfo = useBenchmarkQuery(subject, { suspense: true });
  const benchmarkFund = benchmarkInfo.type === 'fund' ? benchmarkInfo.benchmark : undefined;

  const { rangeDataByFund, primaryRangeAnalysis, benchmarkRange } = useMemo(() => {
    const primaryIndex = analysisRequest?.subjects?.findIndex((s) => s.comparisonType === 'PRIMARY');
    const primaryRangeAnalysis = !isNil(primaryIndex) ? rangeAnalysis?.rangeAnalyses[primaryIndex] : undefined;
    const rangeAnalyses = primaryRangeAnalysis
      ? primaryRangeAnalysis.investmentId
        ? [primaryRangeAnalysis]
        : primaryRangeAnalysis.rangeAnalyses
      : [];
    const rangeDataByFund: Record<SubjectRangeAnalysis['investmentId'], SubjectRangeAnalysis[]> = (() => {
      const result = {};
      rangeAnalyses.forEach((analysis) => {
        if (analysis.investmentId in result) {
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          result[analysis.investmentId].push(analysis);
        } else {
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          result[analysis.investmentId] = [analysis];
        }
      });
      return result;
    })();

    const benchmarkIndex = analysisRequest?.subjects?.findIndex((s) => s.comparisonType === 'BENCHMARK');
    const benchmarkRange = !isNil(benchmarkIndex) ? rangeAnalysis?.rangeAnalyses[benchmarkIndex] : undefined;

    return { rangeDataByFund, primaryRangeAnalysis, benchmarkRange };
  }, [analysisRequest?.subjects, rangeAnalysis?.rangeAnalyses]);

  const uniqFunds = useMemo(() => {
    const funds =
      benchmarkFund && !uniqPortfolioFunds.find((f) => f.id === benchmarkFund.id)
        ? [...uniqPortfolioFunds, benchmarkFund]
        : uniqPortfolioFunds;
    return funds.map((f) => {
      const rangeData = rangeDataByFund[f.id];
      const unproxiedFrequency =
        rangeData && rangeData.length > 0 ? rangeData[rangeData.length - 1]?.frequency : undefined;
      return { ...f, unproxiedFrequency };
    });
  }, [uniqPortfolioFunds, benchmarkFund, rangeDataByFund]);

  return {
    uniqFunds,
    uniqFundIds,
    rangeAnalysis,
    proxyDataByFund: returnsProxyDataByFund,
    holdingsProxyDataByFund,
    benchmarkRange,
    rangeDataByFund,
    primaryRangeAnalysis,
    benchmarkInfo,
    cashRateRange: rangeAnalysis?.cashRateRange,
    referenceRateRange: rangeAnalysis?.referenceRateRange,
  };
};
